<template>

    <div class="pa-2">

        <v-alert v-if="!hasPermission('view projects')" type="error" class="body-2">You don't have permission to View
            Project
        </v-alert>

        <v-card v-if="hasPermission('view projects')"
                flat
                tile
                class="d-flex justify-end pl-3 pr-3 pt-0 pb-0">

            <v-text-field
                class="align-self-center"
                v-model="tableSearch"
                prepend-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                @keyup.enter="searchProjects"
                @click:clear="searchCleared"
                clearable
                solo
                dense
                background-color="grey darken-2"
                style="max-width: 400px;"
            />

            <div class="align-self-center ml-2">
                <v-tooltip color="primary" top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            :disabled="!hasPermission('view projects')"
                            @click="loadProjects()"
                            v-on="on"
                            icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>Reload data</span>
                </v-tooltip>
            </div>

        </v-card>

        <v-data-table
            v-if="hasPermission('view projects')"
            class="mt-4"
            :headers="headers"
            :items="items"
            :server-items-length="serverItemsLength"
            :loading="loading"
            no-data-text="No projects found"
            no-results-text="No projects found"
            :footer-props.sync="footerProps"
            must-sort
            :options.sync="tableOptions"
            @update:options="loadProjects"
        >
            <template v-slot:body="{ items }">
                <tbody>

                <tr v-if="items.length === 0">
                    <td colspan="100">
                        <v-alert
                            color="primary"
                            class="mt-4 body-2"
                            icon="mdi-information">
                            No projects found
                        </v-alert>
                    </td>
                </tr>

                <table-row v-for="item in items" :key="item.id"
                           @edit="editProject"
                           @delete="deleteProject"
                           @clone="cloneProject"
                           @stats="openProjectStats"
                           :columns="headers"
                           :data="item">
                </table-row>

                </tbody>
            </template>

        </v-data-table>
    </div>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';

const {mapState, mapActions, mapMutations, mapGetters} = createNamespacedHelpers('ProjectManager');
import TableRow from './ListProjectsTableRow';

export default {

    components: {
        TableRow
    },

    data() {
        return {

            headers: [
                {name: 'owner', text: 'OWNER', visible: true, sortable: false, width: '70px'},
                {name: 'name', text: 'NAME', visible: true, align: 'left', value: 'name'},
                {
                    name: 'progress_percent',
                    text: 'PROGRESS',
                    visible: true,
                    value: 'completed_percent',
                    width: '120px'
                },
                {name: 'status', text: 'STATUS', visible: true, value: 'status', width: '120px', sortable: false},
                {name: 'dashboard', text: 'DASHBOARD', visible: true, sortable: false, width: '100px'},
                {name: 'response rate', text: 'RESPONSE RATE', visible: true, value: 'response_rate', width: '150px'},
                {
                    name: 'rating',
                    text: 'RATING',
                    visible: true,
                    sortable: true,
                    align: 'center',
                    width: '100px'
                },
                {name: 'calendar', text: 'DEADLINE', width: '150px', visible: true, value: 'end_date'},
                {text: '', align: 'right', value: '', visible: true, width: '40px', sortable: false},

                // Invisible column available for searching
                {name: 'extpid', text: 'external_project', value: 'external_project_reference', visible: false, sortable: false, align: ' d-none'},
                {name: 'extbid', text: 'external_billing', value: 'external_billing_reference', visible: false, sortable: false, align: ' d-none'},
                {name: 'id', text: 'id', value: 'id', visible: false, sortable: false, align: ' d-none'}
            ],

            loading: false,

            totalItems: 0,
            items: [],
            serverItemsLength: 10,

            footerProps: {
                itemsPerPageOptions: [10, 25, 100]
            },
        }
    },

    computed: {

        ...mapState([
            'archivedProjects',
            'archivedTableOptions',
            'archivedTableSearch'
        ]),

        ...mapGetters([
            'getFilteredProjects'
        ]),

        tableOptions: {
            get() {
                return this.archivedTableOptions;
            },
            set(options) {
                this.setArchivedTableOptions(options);
            }
        },
        tableSearch: {
            get() {
                return this.archivedTableSearch;
            },
            set(term) {
                this.setArchivedTableSearch(term);
            }
        }
    },

    mounted() {
        this.loadProjects();
    },

    methods: {

        ...mapMutations([
            'setArchivedTableOptions',
            'setArchivedTableSearch'
        ]),

        ...mapActions([
            'loadArchivedProjects',
        ]),

        editProject(project) {
            this.$emit('edit', project);
        },

        deleteProject(projectId) {
            this.$emit('delete', projectId);
        },

        cloneProject(projectId) {
            this.$emit('clone', projectId);
        },

        searchProjects() {
            this.loadProjects();
        },

        searchCleared() {
            this.tableSearch = null;
            this.loadProjects();
        },

        openProjectStats(project) {
            this.$emit('stats', project);
        },

        loadProjects() {

            this.loading = true;

            const config = {
                sortBy: this.archivedTableOptions.sortBy[0],
                sortDesc: this.archivedTableOptions.sortDesc[0] ? 1 : 0,
                page: this.archivedTableOptions.page,
                itemsPerPage: this.archivedTableOptions.itemsPerPage
            };

            if (this.tableSearch) config.search = this.tableSearch;

            this.loadArchivedProjects(config)
                .then((projects) => {
                    this.items = projects.data;
                    this.totalItems = projects.total;
                    this.serverItemsLength = projects.total;
                })
                .catch(error => {
                    this.$emit('flash-message', {type: 'error', message: 'Archived projects failed to load'});
                })
                .finally(() => {
                    this.loading = false;
                })
        },
    }
}
</script>
